import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import { withMainLayout } from "layout/withMainLayout";
import {
  getSigninErrorMessage,
  NauErrorPayload,
} from "utils/authErrors";
import { setAuthSession } from "utils/authSession";

import styles from "./styles.module.scss";

import BackgroundImage from "assets/img-sunwin/bg-form-1.png";
import LogoSunwin from "assets/img-sunwin/logo.png";
import IconUser from "assets/img-sunwin/icon-user.svg";
import IconLock from "assets/img-sunwin/icon-lock.svg";
import IconEye from "assets/img-sunwin/icon-eye.svg";
import IconEyeActive from "assets/img-sunwin/icon-eye-active.svg";

type NauSigninSuccess = {
  ok?: boolean;
  user_id?: string;
};

const SignInPage: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  const usernameError = submitted && username.trim() === "";
  const passwordError = submitted && password.trim() === "";

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    if (apiError) {
      setApiError(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();

    if (!trimmedUsername || !trimmedPassword) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const response = await fetch("/api/auth/signin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          my_id: {
            username: trimmedUsername,
            password: trimmedPassword,
          },
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        let errorPayload: NauErrorPayload | null = null;
        try {
          errorPayload = responseText ? (JSON.parse(responseText) as NauErrorPayload) : null;
        } catch (parseError) {
          errorPayload = null;
        }

        setApiError(getSigninErrorMessage(response.status, errorPayload));
        return;
      }

      let payload: NauSigninSuccess | null = null;
      try {
        payload = responseText ? (JSON.parse(responseText) as NauSigninSuccess) : null;
      } catch (error) {
        payload = null;
      }

      setAuthSession({ userId: payload?.user_id });
      navigate("/", { replace: true });
    } catch (error) {
      setApiError("Không thể kết nối máy chủ, vui lòng thử lại.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={styles.signInPage}
      style={{ backgroundImage: `url(${BackgroundImage})` }}
    >
      <div className={styles.card}>
        <div className={styles.logo}>
          <img src={LogoSunwin} alt="Sunwin" />
        </div>

        <h1 className={styles.title}>Đăng nhập</h1>
        <p className={styles.subtitle}>Chào mừng quay lại Sunwin</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fieldGroup}>
            <img className={styles.icon} src={IconUser} alt="Username" />
            <input
              className={styles.input}
              type="text"
              placeholder="Tên đăng nhập"
              value={username}
              onChange={handleFieldChange(setUsername)}
              disabled={isSubmitting}
              autoComplete="username"
            />
          </div>
          {usernameError ? (
            <div className={styles.errorText}>Vui lòng nhập tên đăng nhập</div>
          ) : null}

          <div className={styles.fieldGroup}>
            <img className={styles.icon} src={IconLock} alt="Password" />
            <input
              className={styles.input}
              type={showPassword ? "text" : "password"}
              placeholder="Mat khau"
              value={password}
              onChange={handleFieldChange(setPassword)}
              disabled={isSubmitting}
              autoComplete="current-password"
            />
            <button
              type="button"
              className={styles.toggleVisibility}
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              disabled={isSubmitting}
            >
              <img
                src={showPassword ? IconEyeActive : IconEye}
                alt={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              />
            </button>
          </div>
          {passwordError ? (
            <div className={styles.errorText}>Vui lòng nhập mật khẩu</div>
          ) : null}

          <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? "Đang đăng nhập..." : "Đăng nhập"}
          </button>
        </form>

        {apiError ? <div className={styles.apiError}>{apiError}</div> : null}

        <div className={styles.footerLinks}>
          Chưa có tài khoản? <Link to="/">Tạo tài khoản ngay</Link>
        </div>
      </div>
    </div>
  );
};

export default withMainLayout(SignInPage, { hideFooter: true });
